import { NULL_ADDRESS } from "@opensea/utils"
import { useCallback, useState } from "react"
import type { EstimateGasParameters, Hex } from "viem"
import { useConfig, useDeployContract, useSendTransaction } from "wagmi"
import { estimateGas, waitForTransactionReceipt } from "wagmi/actions"
import { useAddress } from "../../hooks/useAddress"
import { useRequireChain } from "../../hooks/useRequireChain"
import {
  type PolychainError,
  parseTransactionError,
  TransactionRevertedError,
} from "../../lib/errors"
import type { Hash, TransactFunction, WaitFunction } from "../../types"
import { useWithConnectorErrorRetry } from "./useWithConnectorErrorRetry"

export const useEvmTransactFunction = () => {
  const config = useConfig()
  const { requireChain } = useRequireChain()
  const address = useAddress()

  const {
    sendTransactionAsync,
    isPending: isPendingTx,
    isIdle: isIdleTx,
    isPaused: isPausedTx,
    isSuccess: isSuccessTx,
    isError: isErrorTx,
    data: dataTx,
  } = useSendTransaction()

  const {
    deployContractAsync,
    isPending: isPendingDeploy,
    isIdle: isIdleDeploy,
    isPaused: isPausedDeploy,
    isSuccess: isSuccessDeploy,
    isError: isErrorDeploy,
    data: dataDeploy,
  } = useDeployContract()

  const [error, setError] = useState<PolychainError>()

  type TransactFunctionParams = Parameters<
    ReturnType<typeof useSendTransaction>["sendTransactionAsync"]
  >[0]

  type DeployContractFunctionParams = Parameters<
    ReturnType<typeof useDeployContract>["deployContractAsync"]
  >[0]

  const withConnectorErrorRetry = useWithConnectorErrorRetry()

  const transact: TransactFunction = useCallback(
    async (variables, { shouldBufferGas = false } = {}) => {
      const { chainId } = variables
      if (!chainId) {
        throw new Error("No chain specified")
      }

      try {
        const hash: Hash = await withConnectorErrorRetry(async () => {
          await requireChain(chainId)

          // Add a buffer to the gas limit if `shouldBufferGas` is true
          let gas = variables.gas
          if (shouldBufferGas) {
            if (gas === undefined) {
              gas = await estimateGas(config, {
                ...(variables as EstimateGasParameters),
                account: (variables.address ?? address) as Hex,
                chainId,
              })
            }
            gas = bufferGas(gas)
          }
          if (variables.to === NULL_ADDRESS) {
            return await deployContractAsync({
              chainId: variables.chainId,
              abi: [],
              bytecode: variables.data,
              gas,
              account: variables.address ?? address,
            } as DeployContractFunctionParams)
          }
          return await sendTransactionAsync({
            ...variables,
            gas,
            account: variables.address ?? address,
          } as TransactFunctionParams)
        })

        setError(undefined)

        const wait: WaitFunction = async opts => {
          const { confirmations = 1, pollingInterval, timeout } = opts || {}
          try {
            const receipt = await waitForTransactionReceipt(config, {
              hash: hash as Hex,
              confirmations,
              pollingInterval,
              timeout,
              chainId,
            })
            if (receipt.status === "success") {
              return {
                status: "success",
                hash,
              }
            }

            return {
              status: "failed",
              hash,
              error: new TransactionRevertedError(
                Error("Transaction Reverted"),
              ),
            }
          } catch (error) {
            return {
              status: "failed",
              hash,
              error: parseTransactionError(error),
            }
          }
        }

        return {
          hash,
          wait,
        }
      } catch (error: unknown) {
        const externalError = parseTransactionError(error)
        setError(externalError)
        throw externalError
      }
    },
    [
      config,
      requireChain,
      sendTransactionAsync,
      withConnectorErrorRetry,
      address,
      deployContractAsync,
    ],
  )

  // Merge state flags for a unified API
  const isPending = isPendingTx || isPendingDeploy
  const isSuccess = isSuccessTx || isSuccessDeploy
  const isError = isErrorTx || isErrorDeploy
  const isIdle = isIdleTx && isIdleDeploy
  const isPaused = isPausedTx || isPausedDeploy
  const data = dataTx ?? dataDeploy

  return {
    transact,
    isPending,
    isSuccess,
    isError,
    isIdle,
    isPaused,
    data,
    error,
  }
}

// Add a 10% buffer to the gas limit
const bufferGas = (gas: bigint) => {
  const multiplier = BigInt(115)
  const divisor = BigInt(100)
  return (gas * multiplier) / divisor
}
